import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './Project.css';
import { useViewTimer } from '../../utils/useViewTimer';

const ProjectDetail = ({
  title,
  caption,
  description,
  year,
  projectUrl,
  tech,
  images,
  inspiration,
  inspiration_url,
  onBack,
  onNext,
  index = 0,
  total = 1,
  gpuEnabled,
}) => {
  const ref = useRef(null);
  useViewTimer(ref, `project: ${title} (detail)`);
  const isInView = useInView(ref, { once: false, margin: '-20% 0px' });
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [title]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i, duration: 0.5, ease: 'easeOut' }
    })
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      key={animationKey}
      ref={ref}
      className="project-detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      <button className="back-button" style={!gpuEnabled ? { cursor: 'pointer' } : {}} onClick={onBack}><span>← </span>Back</button>

      {/* LEFT SIDE */}
      <motion.div
        key={`left-${animationKey}`}
        className="detail-left-fixed"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <motion.h1
          className="detail-title-main fall-in-title"
          custom={0}
          variants={fadeInUp}
        >
          {title.split(' ').map((word, i) => (
            <span key={i} className="fall-in-word" style={{ animationDelay: `${i * 100}ms` }}>
              {word}&nbsp;
            </span>
          ))}
        </motion.h1>

        <motion.p
          className="project-subtitle fade-in-bottom"
          custom={0.3}
          variants={fadeInUp}
        >
          {caption?.toUpperCase()} <span className="divider">/</span> YEAR. {year}
        </motion.p>

        <motion.p
          className="project-description"
          custom={0.6}
          variants={fadeInUp}
        >
          {description}
          {inspiration_url && (
            <div className='clickable'> Inspired by: <a href={inspiration_url} target="_blank" rel="noopener noreferrer">{inspiration}</a></div>
          )}
        </motion.p>

        {projectUrl && (
          <motion.button
            className="visit-button"
            custom={0.9}
            variants={fadeInUp}
            onClick={() => window.open(projectUrl, '_blank', 'noopener,noreferrer')}
          >
            VISIT PROJECT <span className='arrow'>↗</span>
          </motion.button>
        )}
      </motion.div>

      {/* RIGHT SIDE */}
      <motion.div
        key={`right-${animationKey}`}
        className="detail-right-fixed"
        initial="hidden"
        animate="visible"
        variants={fadeInRight}
      >
        <div>
          <motion.div
            className="detail-index"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <span>{String(index + 1).padStart(2, '0')}</span> / {String(total).padStart(2, '0')}
          </motion.div>

          <div className="tech-stack">
            <motion.p
              className="tech-label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              TECH STACK
            </motion.p>

            <ul>
              {tech.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 + i * 0.2, duration: 0.4 }}
                >
                  {item}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* <div onClick={onNext} className="next-project-wrapper">
          <button
            className="next-arrow-button"
            onClick={onNext}
            disabled={index === total - 1}
            aria-label="Next Project"
          >
            <span className="arrow-line">→</span>
          </button>
          <div className="next-project-label">
            <span>NEXT</span>
            <span>PROJECT</span>
          </div>
        </div> */}
      </motion.div>

      {/* GALLERY */}
      <div className="scroll-gallery">
        <div className="vertical-gallery">
          {images.map((src, i) => (
            <motion.div
              key={i}
              className="vertical-gallery-item"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
            >
              <img src={src} alt={`Image ${i + 1}`} className="vertical-gallery-image" />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectDetail;
